import {
    AlertTriangle,
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    Info,
    LoaderCircle,
    Sparkles,
    ToggleLeft,
    ToggleRight,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './CreateSalonHolidayScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatDisplayDate = (isoDate) => {
    if (!isoDate) return ''
    const d = new Date(isoDate + 'T00:00:00')
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}

const CreateSalonHolidayScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [formData, setFormData] = useState({
        holidayDate: '',
        holidayName: '',
        isOptional: false,
    })

    const [isSaving, setIsSaving] = useState(false)
    const [dateError, setDateError] = useState(null)
    const [nameError, setNameError] = useState(null)
    // warnings shown after a success response before navigating back
    const [successWarnings, setSuccessWarnings] = useState([])

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (field === 'holidayDate') setDateError(null)
        if (field === 'holidayName') setNameError(null)
    }

    const createSalonHoliday = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        let hasError = false

        if (!formData.holidayDate) {
            setDateError('Please select a date.')
            hasError = true
        } else {
            setDateError(null)
        }

        if (!formData.holidayName?.trim()) {
            setNameError('Holiday name is required.')
            hasError = true
        } else if (formData.holidayName.trim().length > 200) {
            setNameError('Holiday name must not exceed 200 characters.')
            hasError = true
        } else {
            setNameError(null)
        }

        if (hasError) return

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'createSalonHoliday', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    holidayDate: formData.holidayDate,
                    holidayName: formData.holidayName.trim(),
                    ...(formData.isOptional && { isOptional: 1 }),
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const warnings = respo.response?.warnings || []

                if (warnings.length > 0) {
                    setSuccessWarnings(warnings)
                    warnings.forEach((w) => toast.warning(w.message, { autoClose: 4000 }))
                    setTimeout(() => navigate(-1), 1600)
                } else {
                    toast.success(respo.message || 'Holiday added successfully.')
                    navigate(-1)
                }
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if (respo.message.includes('holidayDate')) {
                        setDateError(respo.message)
                    } else if (respo.message.includes('holidayName')) {
                        setNameError(respo.message)
                    } else {
                        toast.error(respo.message)
                    }
                    break
                case 'VAL_005':
                    setDateError('Please select a valid date.')
                    break
                case 'VAL_002':
                    setNameError(respo.message)
                    break
                case 'VAL_004':
                    setDateError(
                        `"${respo.response?.existingHolidayName}" already exists on this date.`
                    )
                    break
                case 'VAL_008':
                    setDateError(respo.message)
                    break
                case 'PAY_003':
                    setDateError('This date is in a finalized payroll cycle and cannot be changed.')
                    break
                default:
                    toast.error(respo?.message || 'Failed to add holiday. Please try again.')
            }
        } catch (error) {
            console.error('createSalonHoliday error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const nameLength = formData.holidayName.length
    const today = new Date().toISOString().split('T')[0]

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <header className={styles.header}>
                <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => navigate(-1)}
                    disabled={isSaving}
                >
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.headerPill}>
                        <Sparkles size={13} /> Add Holiday
                    </span>
                    <h1 className={styles.headerTitle}>New Salon Holiday</h1>
                </div>
            </header>

            {/* ── Form card ── */}
            <section className={styles.card}>

                {/* Date field */}
                <div className={`${styles.fieldGroup} ${dateError ? styles.fieldGroupError : ''}`}>
                    <label className={styles.label}>
                        <CalendarDays size={14} /> Holiday Date <span className={styles.required}>*</span>
                    </label>

                    <div className={styles.datePickerWrap}>
                        <input
                            type="date"
                            className={styles.dateInput}
                            value={formData.holidayDate}
                            min="2020-01-01"
                            max="2035-12-31"
                            onChange={(e) => handleChange('holidayDate', e.target.value)}
                        />
                        {formData.holidayDate && (
                            <span className={styles.datePretty}>
                                {formatDisplayDate(formData.holidayDate)}
                            </span>
                        )}
                    </div>

                    {dateError && (
                        <p className={styles.errorText}>
                            <AlertTriangle size={13} /> {dateError}
                        </p>
                    )}
                </div>

                {/* Name field */}
                <div className={`${styles.fieldGroup} ${nameError ? styles.fieldGroupError : ''}`}>
                    <label className={styles.label}>
                        Holiday Name <span className={styles.required}>*</span>
                    </label>

                    <input
                        type="text"
                        className={styles.textInput}
                        placeholder="e.g. Salon Anniversary, Local Festival"
                        value={formData.holidayName}
                        maxLength={200}
                        onChange={(e) => handleChange('holidayName', e.target.value)}
                    />

                    {nameLength > 150 && (
                        <p className={`${styles.charCounter} ${nameLength >= 190 ? styles.charCounterWarn : ''}`}>
                            {nameLength} / 200
                        </p>
                    )}

                    {nameError && (
                        <p className={styles.errorText}>
                            <AlertTriangle size={13} /> {nameError}
                        </p>
                    )}
                </div>

                {/* Optional toggle */}
                <div className={styles.toggleGroup}>
                    <div className={styles.toggleTop}>
                        <div className={styles.toggleLabelCol}>
                            <span className={styles.label}>Optional Holiday</span>
                            <p className={styles.toggleHelper}>
                                {formData.isOptional
                                    ? 'Optional — informational only. Does not affect working day count. Staff may or may not avail it.'
                                    : 'Mandatory — excluded from working day count. Staff are paid for this day without marking leave.'}
                            </p>
                        </div>
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            onClick={() => handleChange('isOptional', !formData.isOptional)}
                            aria-pressed={formData.isOptional}
                        >
                            {formData.isOptional
                                ? <ToggleRight size={34} className={styles.toggleOn} />
                                : <ToggleLeft size={34} className={styles.toggleOff} />}
                        </button>
                    </div>

                    <div className={`${styles.toggleBadge} ${formData.isOptional ? styles.toggleBadgeOptional : styles.toggleBadgeMandatory}`}>
                        <Info size={13} />
                        {formData.isOptional ? 'Optional' : 'Mandatory'}
                    </div>
                </div>
            </section>

            {/* ── Warnings shown after success before nav ── */}
            {successWarnings.length > 0 && (
                <section className={styles.warningBanner}>
                    {successWarnings.map((w, i) => (
                        <div key={i} className={styles.warningRow}>
                            <AlertTriangle size={14} className={styles.warningIcon} />
                            <p>{w.message}</p>
                        </div>
                    ))}
                </section>
            )}

            {/* ── Info box ── */}
            <section className={styles.infoBox}>
                <CheckCircle2 size={15} className={styles.infoIcon} />
                <p>
                    Salon-specific holidays are layered on top of platform holidays. They count towards
                    your store's working-day calendar and affect payroll calculations.
                </p>
            </section>

            {/* ── Save button ── */}
            <button
                type="button"
                className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                onClick={createSalonHoliday}
                disabled={isSaving}
            >
                {isSaving
                    ? <><LoaderCircle size={16} className={styles.spin} /> Adding…</>
                    : <><CalendarDays size={16} /> Add Holiday</>}
            </button>
        </div>
    )
}

export default CreateSalonHolidayScreen